import {
  MIRA_PERIOD_ROLLING_12,
  MIRA_PERIOD_ROLLING_12_PREVIOUS,
  MIRA_PERIOD_YEAR_PREFIX,
  MIRA_PERIOD_YEAR_TO_DATE,
  MIRA_SECTIONS,
  MiraAvailabilityState,
  MiraSection,
  MiraSectionDocument,
} from "@/data/models/Mira";

/**
 * The figures Mira retired on 2026-10-13 (#2610040, Hans's "Mira is a look back"): the four "Stand heute"
 * snapshot metrics IRES Cloud no longer publishes (Stehende Projekte PHA-008, Offene Zuordnungen auf
 * abgeschlossenen Projekten DEV-007, Offene Gerätezuordnungen und Trocknungen DQ-003, Nicht auflösbare
 * Kategorie / Template DQ-004). Kept as a small ignore list because a document published before the Cloud
 * deploy can still carry them until the next night; the view layer must never treat such a leftover as a
 * normal period tile, an unlock, or an availability-ring figure.
 */
export const MIRA_RETIRED_METRIC_IDS = ["MIRA-PHA-008", "MIRA-DEV-007", "MIRA-DQ-003", "MIRA-DQ-004"] as const;

/** Whether a metric id is one of the retired "Stand heute" figures the view layer ignores (#2610040). */
export function isRetiredMetric(metricId: string): boolean {
  return (MIRA_RETIRED_METRIC_IDS as readonly string[]).includes(metricId);
}

/**
 * Figures IRES Cloud still publishes but no IRES Office screen shows, by decision: the Prozess cards
 * Hans had removed (Phasendauer PHA-001, Wartezeit PHA-002, Reihenfolge PHA-009; 2026-10-05), the
 * "Dauer bis" tiles (PHA-003 to PHA-007) and the Projektablauf card (PHA-011) the transitions replaced
 * (2026-10-13), the conventional drying time DRY-010 replaced (DRY-003, 2026-10-13), and
 * Wiederherstellungsdauer (PHA-020), hidden until IRES Project records a real restoration start (Hans,
 * 2026-10-14), and Flottenauslastung's single headline the four per-fleet figures replaced (DEV-006, #2610072,
 * still drawn from a document that has no per-fleet figures), Messfrequenz per week, which Zwischenmessungen
 * je Projekt replaced (DRY-005, #2610112 X5, still drawn from a document without DRY-012), and Geräte je
 * Projekt by kind of client (DEV-017 / DEV-018, #2610112 X7), which only choose the Markt for Geräte je
 * Projekt. A figure nobody can see is never offered to unlock, nor counted as available.
 */
export const MIRA_HIDDEN_METRIC_IDS = [
  "MIRA-PHA-001", "MIRA-PHA-002", "MIRA-PHA-003", "MIRA-PHA-004", "MIRA-PHA-005", "MIRA-PHA-006",
  "MIRA-PHA-007", "MIRA-PHA-009", "MIRA-PHA-011", "MIRA-PHA-020", "MIRA-DRY-003", "MIRA-DEV-006",
  "MIRA-DRY-005", "MIRA-DEV-017", "MIRA-DEV-018",
] as const;

/** Whether a published figure has a place on a screen: neither retired nor hidden. */
export function isShownMetric(metricId: string): boolean {
  return !isRetiredMetric(metricId) && !(MIRA_HIDDEN_METRIC_IDS as readonly string[]).includes(metricId);
}

/**
 * The logic behind the Mira Datenqualität screen, kept out of the component so it can be tested
 * without mounting anything. See `docs/analytics/mira-office-screens.md`.
 */

/** Where a period sorts in the picker: the rolling year first, calendar years newest first last. */
function periodRank(key: string): number {
  if (key === MIRA_PERIOD_ROLLING_12) return 0;
  if (key === MIRA_PERIOD_ROLLING_12_PREVIOUS) return 1;
  if (key === MIRA_PERIOD_YEAR_TO_DATE) return 2;
  return 3;
}

/** The calendar year of a `year-<yyyy>` period, or `null` for any other period. */
export function periodYear(key: string): string | null {
  return key.startsWith(MIRA_PERIOD_YEAR_PREFIX) ? key.substring(MIRA_PERIOD_YEAR_PREFIX.length) : null;
}

/**
 * The first year any figure of the document has a case in (`"2021"`), or `null` where none has. IRES
 * Cloud publishes every year since 2017 (Track #2610040, "years: all"); a client's year axis and year
 * picker start at its own first year instead, so a client that began in 2024 is not shown seven empty
 * years. A later year with no published value stays on the axis as a gap (Taimoor, 2026-10-13).
 */
export function firstYearWithCases(trends: MiraSectionDocument["trends"] | undefined): string | null {
  let first: string | null = null;
  for (const series of Object.values(trends ?? {})) {
    for (const point of series?.year ?? []) {
      if ((point.basis ?? 0) > 0 && (first === null || point.key < first)) {
        first = point.key;
      }
    }
  }
  return first;
}

/**
 * The periods a document carries, in the order the picker offers them.
 *
 * Read off the document rather than hardcoded, so a period the job adds or drops never leaves the
 * picker offering something that is not there. Unknown keys are left out: the picker has no label
 * for them.
 *
 * @param document - any section document; all sections carry the same periods.
 * @returns the period keys, the rolling year first.
 */
export function availablePeriods(document: MiraSectionDocument | undefined): string[] {
  if (!document) {
    return [];
  }

  // A calendar year before the client's first year with any case is left out (#2610040): IRES Cloud
  // publishes every year since 2017, and a picker offering seven empty years is noise.
  const firstYear = firstYearWithCases(document.trends);
  return Object.keys(document.periods)
    .filter((key) => periodRank(key) < 3 || /^year-\d{4}$/.test(key))
    .filter((key) => {
      const year = periodYear(key);
      return firstYear === null || year === null || year >= firstYear;
    })
    .sort((a, b) => periodRank(a) - periodRank(b) || b.localeCompare(a));
}

/**
 * The periods any of the documents offers, in the picker's order: the Mira landing page's picker
 * (#2610137), whose cards come from every section, so a year one section has is offered there.
 */
export function availablePeriodsAcross(documents: Array<MiraSectionDocument | undefined>): string[] {
  const keys = new Set<string>();
  documents.forEach((document) => availablePeriods(document).forEach((key) => keys.add(key)));
  return Array.from(keys).sort((a, b) => periodRank(a) - periodRank(b) || b.localeCompare(a));
}

/** A period's name for a picker and a header: its year ("2025"), else its translated name. */
export function miraPeriodName(period: string, t: (key: string) => string): string {
  return periodYear(period) ?? t(`mira.periods.${period}`);
}

/** The metrics one section cannot show in a period for lack of data, in document order. */
export interface MiraSectionUnlocks {
  section: MiraSection;
  metricIds: string[];
}

/**
 * Every metric that is hidden because the client has too little data for it, by section.
 *
 * **Only `INSUFFICIENT_DATA`.** That is the one state the client can change by capturing more
 * (plan §5.7). `UNMEASURED` is a gap in our pipeline, and listing it would tell a client to fix
 * something that is ours; `SUPPRESSED` and `NOT_APPLICABLE` are not the client's to unlock at all.
 *
 * Read from the chosen period's metrics rather than the document's `unlocks` list, which describes
 * the rolling year only and names the field in Cloud's German.
 *
 * @param sections - the section documents that exist.
 * @param period - the period key the screen shows.
 * @returns one entry per section with anything to unlock, in Mira's section order.
 */
export function collectUnlocks(
  sections: Partial<Record<MiraSection, MiraSectionDocument>>,
  period: string
): MiraSectionUnlocks[] {
  return MIRA_SECTIONS.map((section) => {
    const metrics = sections[section]?.periods[period]?.metrics ?? {};

    return {
      section,
      metricIds: Object.keys(metrics)
        .filter((id) => isShownMetric(id) && metrics[id].state === MiraAvailabilityState.INSUFFICIENT_DATA)
        .sort(),
    };
  }).filter((entry) => entry.metricIds.length > 0);
}

/** One field of the Feldabdeckung figure. */
export interface MiraFieldShare {
  field: string;
  share: number;
}

/**
 * The fields behind `MIRA-DQ-001`, weakest first.
 *
 * The headline is the weakest field, so the list starts where the headline does. Ties keep the
 * field name order, so the list does not reshuffle between two equal fields on each load.
 *
 * @param breakdown - field -> share of projects with it filled in, 0 to 1.
 */
export function rankFields(breakdown: Record<string, number> | undefined): MiraFieldShare[] {
  return Object.entries(breakdown ?? {})
    .map(([field, share]) => ({ field, share }))
    .sort((a, b) => a.share - b.share || a.field.localeCompare(b.field));
}

/** A share as the screen states it: a whole percentage, and whether it had to be clamped. */
export interface MiraPercent {
  value: number;
  /** `below` renders as "< 1 %", `above` as "> 99 %". */
  bound: "below" | "above" | null;
}

/**
 * A share of 0 to 1 as a whole percentage that never overstates.
 *
 * Plain rounding would print 99.9% as "100 %" and 0.2% as "0 %", and on a data-quality screen both
 * are exactly the wrong message: "complete" for a field with gaps, "never" for one that is sometimes
 * filled in. Only a share that really is 0 or 1 reads as 0 or 100.
 */
export function shareToPercent(share: number): MiraPercent {
  if (share > 0 && share < 0.01) {
    return { value: 1, bound: "below" };
  }

  if (share > 0.99 && share < 1) {
    return { value: 99, bound: "above" };
  }

  return { value: Math.round(share * 100), bound: null };
}

/**
 * A period's first and last day, for the header.
 *
 * Parsed as local calendar dates, not with `new Date("YYYY-MM-DD")`, which reads the string as UTC
 * midnight and shows the previous day west of Greenwich. `toExclusive` is the day after the period.
 */
export function periodRange(from: string, toExclusive: string): { first: Date; last: Date } {
  const parse = (value: string) => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const last = parse(toExclusive);
  last.setDate(last.getDate() - 1);

  return { first: parse(from), last };
}

/**
 * Mira's own coverage floor: below it, a figure that depends on the field is hidden (catalog S8).
 * The Datenqualität screen marks a share below it critical (Taimoor, 2026-09-30).
 */
export const MIRA_COVERAGE_FLOOR = 0.4;
/** From here a coverage counts as good (Taimoor, 2026-09-30). */
export const MIRA_COVERAGE_GOOD = 0.8;

/** How a coverage share is judged. */
export enum MiraCoverageStatus {
  GOOD = "good",
  WARNING = "warning",
  CRITICAL = "critical",
}

/**
 * The status of a coverage share: critical below {@link MIRA_COVERAGE_FLOOR}, where Mira hides the
 * figures that need the field, good from {@link MIRA_COVERAGE_GOOD}, a warning in between.
 */
export function coverageStatus(share: number): MiraCoverageStatus {
  if (share < MIRA_COVERAGE_FLOOR) {
    return MiraCoverageStatus.CRITICAL;
  }

  return share >= MIRA_COVERAGE_GOOD ? MiraCoverageStatus.GOOD : MiraCoverageStatus.WARNING;
}

/** A coverage change against the comparison period, in whole percentage points. */
export interface MiraCoverageDelta {
  points: number;
  direction: "up" | "down" | "flat";
}

/**
 * The change of a coverage share against the comparison period, or `null` where there is nothing
 * to compare with: a period with no comparison (this year, the oldest year), or a comparison period
 * that published no value.
 *
 * Whole points of the difference of the shares, not the difference of two rounded percentages, so
 * 79.6 % against 80.4 % reads as a change of -1 rather than 0. For coverage, up is always good.
 */
export function coverageDelta(
  current: number | null | undefined,
  previous: number | null | undefined
): MiraCoverageDelta | null {
  if (current === null || current === undefined || previous === null || previous === undefined) {
    return null;
  }

  // `|| 0` turns the -0 that Math.round gives a small drop (-0.2 -> -0) into a plain 0.
  const points = Math.round((current - previous) * 100) || 0;
  return { points, direction: points > 0 ? "up" : points < 0 ? "down" : "flat" };
}
